import { Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { lazyWithRetry as lazy } from './lib/lazyWithRetry';
import { useAuthStore } from './stores/authStore';
import { useSessionRestore } from './api/auth';
import { AppShell } from './components/layout/AppShell';
import { AppSkeleton } from './components/layout/AppSkeleton';
import { SuperAdminShell } from './components/layout/SuperAdminShell';
import { FullSpinner } from './components/ui/Spinner';

const Login = lazy(() => import('./pages/auth/Login').then((m) => ({ default: m.Login })));
const Signup = lazy(() => import('./pages/auth/Signup').then((m) => ({ default: m.Signup })));
const OnboardingWizard = lazy(() =>
  import('./pages/onboarding/OnboardingWizard').then((m) => ({ default: m.OnboardingWizard })),
);
const Dashboard = lazy(() =>
  import('./pages/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const CatalogList = lazy(() =>
  import('./pages/catalog/CatalogList').then((m) => ({ default: m.CatalogList })),
);
const ProductForm = lazy(() =>
  import('./pages/catalog/ProductForm').then((m) => ({ default: m.ProductForm })),
);
const BulkImport = lazy(() =>
  import('./pages/catalog/BulkImport').then((m) => ({ default: m.BulkImport })),
);
const AISettings = lazy(() =>
  import('./pages/ai-settings/AISettings').then((m) => ({ default: m.AISettings })),
);
const Conversations = lazy(() =>
  import('./pages/conversations/Conversations').then((m) => ({ default: m.Conversations })),
);
const Contacts = lazy(() =>
  import('./pages/contacts/Contacts').then((m) => ({ default: m.Contacts })),
);
const Channels = lazy(() =>
  import('./pages/channels/Channels').then((m) => ({ default: m.Channels })),
);
const Orders = lazy(() => import('./pages/orders/Orders').then((m) => ({ default: m.Orders })));
const Bookings = lazy(() =>
  import('./pages/bookings/Bookings').then((m) => ({ default: m.Bookings })),
);
const Availability = lazy(() =>
  import('./pages/availability/Availability').then((m) => ({ default: m.Availability })),
);
const Staff = lazy(() => import('./pages/staff/Staff').then((m) => ({ default: m.Staff })));
const Payments = lazy(() =>
  import('./pages/payments/Payments').then((m) => ({ default: m.Payments })),
);
const Analytics = lazy(() =>
  import('./pages/analytics/Analytics').then((m) => ({ default: m.Analytics })),
);
const Delivery = lazy(() =>
  import('./pages/delivery/Delivery').then((m) => ({ default: m.Delivery })),
);
const Billing = lazy(() =>
  import('./pages/billing/Billing').then((m) => ({ default: m.Billing })),
);
const SettingsLayout = lazy(() =>
  import('./pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
);
const SettingsGeneral = lazy(() =>
  import('./pages/settings/sections/General').then((m) => ({ default: m.General })),
);
const SettingsBranding = lazy(() =>
  import('./pages/settings/sections/Branding').then((m) => ({ default: m.Branding })),
);
const SettingsAccount = lazy(() =>
  import('./pages/settings/sections/Account').then((m) => ({ default: m.Account })),
);
const SettingsInstall = lazy(() =>
  import('./pages/settings/sections/Install').then((m) => ({ default: m.Install })),
);
const NotificationSettings = lazy(() =>
  import('./pages/settings/Notifications').then((m) => ({ default: m.Notifications })),
);

// Super-admin (platform staff) console.
const SuperOverview = lazy(() =>
  import('./pages/superadmin/Overview').then((m) => ({ default: m.SuperOverview })),
);
const SuperTenants = lazy(() =>
  import('./pages/superadmin/Tenants').then((m) => ({ default: m.SuperTenants })),
);
const SuperTenantDetail = lazy(() =>
  import('./pages/superadmin/TenantDetail').then((m) => ({ default: m.SuperTenantDetail })),
);
const SuperUsers = lazy(() =>
  import('./pages/superadmin/Users').then((m) => ({ default: m.SuperUsers })),
);
const SuperConversations = lazy(() =>
  import('./pages/superadmin/Conversations').then((m) => ({ default: m.SuperConversations })),
);
const SuperOrders = lazy(() =>
  import('./pages/superadmin/Orders').then((m) => ({ default: m.SuperOrders })),
);
const SuperProducts = lazy(() =>
  import('./pages/superadmin/Products').then((m) => ({ default: m.SuperProducts })),
);
const SuperCurrencies = lazy(() =>
  import('./pages/superadmin/Currencies').then((m) => ({ default: m.SuperCurrencies })),
);

function ProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { token, restoring, restored } = useSessionRestore();

  if (token || restored) return <>{children}</>;
  if (restoring) return <AppSkeleton />;

  return <Navigate to="/login" state={{ from: location }} replace />;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.accessToken);
  const role = useAuthStore((s) => s.user?.role);
  if (token) return <Navigate to={role === 'superadmin' ? '/superadmin' : '/'} replace />;
  return <>{children}</>;
}

/** Platform-staff-only guard. Restores the session like ProtectedRoute, then
 *  requires the `superadmin` role. */
function SuperAdminRoute({ children }: { children: ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();
  const { token, restoring, restored } = useSessionRestore();

  if (restoring) return <AppSkeleton />;
  if (!token && !restored) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated but not a super-admin → send to the merchant app.
  if ((role ?? useAuthStore.getState().user?.role) !== 'superadmin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<FullSpinner />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnly>
              <Login />
            </PublicOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnly>
              <Signup />
            </PublicOnly>
          }
        />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingWizard />
            </ProtectedRoute>
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/catalog" element={<CatalogList />} />
          <Route path="/catalog/new" element={<ProductForm />} />
          <Route path="/catalog/import" element={<BulkImport />} />
          <Route path="/catalog/:id" element={<ProductForm />} />
          <Route path="/ai-settings" element={<AISettings />} />
          <Route path="/conversations" element={<Conversations />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/bookings" element={<Bookings />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/delivery" element={<Delivery />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<SettingsGeneral />} />
            <Route path="channels" element={<Channels />} />
            <Route path="branding" element={<SettingsBranding />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="team" element={<SettingsAccount />} />
            <Route path="install" element={<SettingsInstall />} />
          </Route>
        </Route>

        <Route
          path="/superadmin"
          element={
            <SuperAdminRoute>
              <SuperAdminShell />
            </SuperAdminRoute>
          }
        >
          <Route index element={<SuperOverview />} />
          <Route path="tenants" element={<SuperTenants />} />
          <Route path="tenants/:id" element={<SuperTenantDetail />} />
          <Route path="users" element={<SuperUsers />} />
          <Route path="conversations" element={<SuperConversations />} />
          <Route path="orders" element={<SuperOrders />} />
          <Route path="products" element={<SuperProducts />} />
          <Route path="currencies" element={<SuperCurrencies />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
